import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useAppConfig } from 'src/modules/appConfig';
import * as Schema from 'src/types/graphql';

import { useQuery, useToastMutation } from '../../../modules/apollo';
import type { Viewer } from '../../../types';
import type { LanguageProficiency } from '../../../types/graphql';
import { useGetConfirmedRoles } from '../CareerHistory/data';
import { useGetConfirmedQualifications } from '../Education/data';
import { getLanguages } from '../Languages/languages.graphql';
import { getLicences } from '../Licences/licences.graphql';
import type { GraphqlLicence } from '../Licences/types';
import { usePersonalDetails } from '../PersonalDetails/data';
import { useSkills } from '../Skills/data';
import { useSummary } from '../Summary/data';

import translations from './.vocab';
import {
  generateResume,
  generateResumeFailure,
  generateResumeSuccess,
} from './actionCreators';

export const useResumeData = () => {
  const { personalDetails } = usePersonalDetails();
  const firstName = personalDetails?.personalDetails.firstName || '';
  const lastName = personalDetails?.personalDetails.lastName || '';
  const phoneNumber = personalDetails?.personalDetails.phoneNumber || '';

  const { confirmedRoles: careerHistory } = useGetConfirmedRoles();
  const { summary } = useSummary();
  const { skills } = useSkills();

  const { confirmedQualifications: education } =
    useGetConfirmedQualifications();

  const { language, zone } = useAppConfig();

  const { data: viewerLicences } = useQuery<
    Viewer<{ licences: GraphqlLicence[] }>
  >(getLicences, { variables: { languageCode: language, zone } });

  const licences = useMemo(
    () => viewerLicences?.viewer?.licences ?? [],
    [viewerLicences?.viewer?.licences],
  );

  const { data: viewerLanguages } =
    useQuery<Viewer<{ languageProficiencies: LanguageProficiency[] }>>(
      getLanguages,
    );

  const languages = useMemo(
    () => viewerLanguages?.viewer?.languageProficiencies ?? [],
    [viewerLanguages?.viewer?.languageProficiencies],
  );

  return {
    firstName,
    lastName,
    phoneNumber,
    summary,
    careerHistory,
    education,
    skills,
    languages,
    licences,
  };
};

const key = 'create-resume';

export const useGenerateResumeFromProfile = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const onMutate = () => {
    dispatch(generateResume({ isDefault: false }));
  };

  const onCompleted = (data: Schema.GenerateResumeFromProfileMutation) => {
    dispatch(
      generateResumeSuccess({
        newResumeId: data.generateResumeFromProfile?.resume?.id || '',
        isDefault: data.generateResumeFromProfile?.resume?.isDefault || false,
      }),
    );
  };

  const onError = (error: ApolloError) => {
    dispatch(generateResumeFailure(error));
  };

  return useToastMutation(Schema.GenerateResumeFromProfileDocument, {
    key,
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Resumé created'),
    failToastMessage: t('Resumé could not be created'),
  });
};
